import { normalizeDocumentMetadata, type DocumentMetadata } from '../../services/pages/documentMetadata'
import type { NodeSearchResult } from '../../services/corpus/corpusSearch'
import type { FrontmatterValues, PublishingProfile } from '../../services/importExport/blogPublishing'
import type { PageSetup } from '../../utils/pageSetup'

// What the corpus is made of: its nodes (desks, folders, pages and notes),
// the Trash, and the shapes the panel and overview use.

export interface CorpusNode {
  id: string
  name: string
  subtype: 'desk' | 'folder' | 'page'
  children?: CorpusNode[]
  expanded?: boolean
  position: number
  hlcTimestamp: number
  icon?: string | null
  color?: string | null
  /** Alternate names used by project lookup. Titles are always indexed separately. */
  aliases?: string[]
  /** Undefined is treated as true for manifests created before document metadata existed. */
  includeInExport?: boolean
  /** Undefined is treated as true for manifests created before document metadata existed. */
  includeInWordCount?: boolean
  /** Pages: base face inherited by text without an explicit font mark. */
  fontFamily?: string
  /** Pages: blog front matter values the writer has confirmed. */
  frontmatter?: FrontmatterValues
  /** Pages: where the post was last published, relative to the publish folder. */
  publishedFile?: string
  /** Desks and folders: static-site publishing profile for pages inside. */
  publishing?: PublishingProfile
  /** Pages: a line or two on what the page is for, shown on the overview. */
  synopsis?: string
  /** Pages: id of a status from Settings → Documents → Page Statuses. */
  status?: string
  /** Any node: the day it is due, 'YYYY-MM-DD'. */
  due?: string
  /** Pages: the page view's layout and the export default. Folders and desks: the last one applied inside. */
  pageSetup?: PageSetup
  isUnsaved?: boolean
}

export function getDocumentMetadata(node: CorpusNode): DocumentMetadata {
  return {
    aliases: Array.isArray(node.aliases) ? node.aliases : [],
    includeInExport: node.includeInExport !== false,
    includeInWordCount: node.includeInWordCount !== false,
    ...(node.fontFamily ? { fontFamily: node.fontFamily } : {}),
    ...(node.frontmatter ? { frontmatter: node.frontmatter } : {}),
    ...(node.publishedFile ? { publishedFile: node.publishedFile } : {}),
    ...(node.publishing ? { publishing: node.publishing } : {}),
    ...(node.synopsis ? { synopsis: node.synopsis } : {}),
    ...(node.status ? { status: node.status } : {}),
    ...(node.due ? { due: node.due } : {}),
    ...(node.pageSetup ? { pageSetup: node.pageSetup } : {}),
  }
}

/** Node fields carried by a synced metadata payload. */
export function metadataNodeFields(raw: any): Partial<Pick<CorpusNode, 'aliases' | 'includeInExport' | 'includeInWordCount' | 'fontFamily' | 'frontmatter' | 'publishedFile' | 'publishing' | 'synopsis' | 'status' | 'due' | 'pageSetup'>> {
  const metadata = normalizeDocumentMetadata(raw)
  return {
    aliases: metadata.aliases,
    includeInExport: metadata.includeInExport,
    includeInWordCount: metadata.includeInWordCount,
    ...(metadata.fontFamily ? { fontFamily: metadata.fontFamily } : {}),
    frontmatter: metadata.frontmatter,
    publishedFile: metadata.publishedFile,
    publishing: metadata.publishing,
    synopsis: metadata.synopsis,
    status: metadata.status,
    due: metadata.due,
    pageSetup: metadata.pageSetup,
  }
}

export interface TrashEntry {
  node: CorpusNode
  originalParentId: string | null
  originalIndex: number
  deletedAt: number
}

/** Where a node sat in the tree, so a move can be put back exactly. */
export interface NodePlacement {
  id: string
  parentId: string | null
  index: number
  position: number
}

export type SearchView = 'list' | 'tree'

/** How the overview of a folder or desk lays out its contents. One choice for every folder. */
export type OverviewView = 'cards' | 'list' | 'outline'
export type OverviewSort = 'manual' | 'name' | 'words' | 'written' | 'status' | 'due'
/** Which lines of a page its overview card previews: the opening, or where the writing stops. */
export type OverviewPreview = 'start' | 'end'
export const OVERVIEW_SORTS: OverviewSort[] = ['manual', 'name', 'words', 'written', 'status', 'due']

/**
 * Where a search (and a replace) looks. 'view' is the default: the desk being
 * viewed, or the whole corpus when the query asks for it (scope:all, desk:).
 */
export type SearchScope = { kind: 'view' } | { kind: 'corpus' } | { kind: 'node'; id: string }

export interface FlatSearchResult {
  node: CorpusNode
  /** Ancestor names below the current desk, outermost first. */
  path: string[]
  result: NodeSearchResult
}
